// TreckDeductionPdf.tsx
//
// Public API for the Treck deduction PDF export. The document itself is built by
// TreckDeductionPdfDoc.ts using jsPDF + jspdf-autotable, which renders even large
// tables in a few hundred milliseconds — no Yoga layout engine and no Web Worker
// required.
//
// This replaces the previous @react-pdf/renderer implementation, whose
// pdf().toBlob() ran the entire flexbox layout + render pipeline synchronously
// on the main thread. For deduction reports with many line items that froze the
// tab for several seconds while the PDF was created.
//
// A single macrotask yield before the (now fast) synchronous build lets React
// commit any pending loading state and lets the browser paint first, so the UI
// stays responsive.
import { saveAs } from 'file-saver'
import { buildPdfBlob } from './TreckDeductionPdfDoc'
import type { GeneratePdfParams } from './TreckDeductionPdfDoc'

export type { GeneratePdfParams } from './TreckDeductionPdfDoc'

/** Let React commit any loading state and the browser paint before we build. */
function yieldToBrowser(): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, 0))
}

/** Returns a File object (for use as an attachment) without triggering a download. */
export async function buildPdfFile(
  params: GeneratePdfParams,
): Promise<File | undefined> {
  const { vendorId, documentId } = params
  try {
    await yieldToBrowser()
    const blob = buildPdfBlob(params)
    const filename = `report_${vendorId ?? ''}_${documentId ?? ''}.pdf`
    return new File([blob], filename, { type: 'application/pdf' })
  } catch (error) {
    console.error('Error building PDF file:', error)
    return undefined
  }
}

export async function generatePdfFile(
  params: GeneratePdfParams,
): Promise<void> {
  const { vendorId, documentId } = params
  try {
    await yieldToBrowser()
    const blob = buildPdfBlob(params)
    const filename = `report_${vendorId ?? ''}_${documentId ?? ''}.pdf`
    saveAs(blob, filename)
  } catch (error) {
    console.error('Error generating PDF file:', error)
    throw error
  }
}
